import { FC, Fragment } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete, Divider, FormInputText, InputLabel, Paper } from '@hopskipdrive/react-components';

import { useReportsQuery } from '../../../hooks/useReportsQuery';

import { useRiders } from './useRiders';

interface RidersFilterProps {
  customReport?: boolean;
}

export const RidersFilter: FC<RidersFilterProps> = ({ customReport = false }) => {
  const inputId = customReport ? 'rider-multi-select-modal' : 'rider-multi-select';
  const { staticFilters } = useReportsQuery();
  const { riders, ridersLoading, deboucedQueryRiders, getRiderOptions } = useRiders();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const isSlug = (value) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);

  const combinedOptions = [...(staticFilters?.riders || []), ...riders].map((option) => ({
    ...option,
    group: isSlug(option.value) ? 'riders' : 'predefined',
  }));

  return (
    <>
      <InputLabel id={`${inputId}-label`} htmlFor={inputId}>
        Riders
      </InputLabel>
      <Controller
        name="riders"
        control={control}
        defaultValue={[]}
        render={({ field: { value, onChange } }) => (
          <Autocomplete
            id={inputId}
            ListboxProps={{ 'aria-labelledby': `${inputId}-label` }}
            className={!!errors.riders ? 'hasError' : ''}
            value={value}
            multiple
            getOptionDisabled={(option) => {
              const currentValues = value.map((option) => ({
                ...option,
                group: isSlug(option.value) ? 'riders' : 'predefined',
              }));

              return getOptionDisabled(option, { value: currentValues });
            }}
            onOpen={() => getRiderOptions()}
            isLoading={ridersLoading}
            data-testid="rider-multi-select"
            options={combinedOptions}
            groupBy={(option) => option.group}
            renderGroup={(params) => (
              <Fragment key={params.key}>
                {params.group === 'riders' && (
                  <li role="presentation" aria-hidden="true">
                    <Divider />
                  </li>
                )}
                {params.children}
              </Fragment>
            )}
            isOptionEqualToValue={(option, value) => riders.length === 0 || option.value === value.value}
            renderInput={(params) => (
              <FormInputText
                {...params}
                variant="outlined"
                onChange={(e) => {
                  deboucedQueryRiders(e.target.value);
                }}
                error={!!errors.riders}
                helperText={errors.riders?.message as string}
              />
            )}
            onChange={(_, data) => onChange(data)}
            PaperComponent={({ children, ...props }) => (
              <Paper data-testid="options-listbox" {...props}>
                {children}
              </Paper>
            )}
          />
        )}
      />
    </>
  );
};

const getOptionDisabled = (option, { value = [] }: { value?: Array<{ value: string; group: string }> } = {}) => {
  const isApiOption = option.group === 'riders';
  const isPredefinedOption = option.group === 'predefined';

  if (isApiOption) {
    // Disable the entire 'predefined' group when any API option ('riders') is selected
    return value.some((selectedOption) => selectedOption.group === 'predefined');
  }
  if (isPredefinedOption) {
    // Disable all other predefined options when a predefined option is selected
    return value.some(
      (selectedOption) =>
        (selectedOption.group === 'predefined' && selectedOption.value !== option.value) ||
        selectedOption.group === 'riders'
    );
  }

  return false;
};
